import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { createLoan, getLoanById, updateLoan } from "@/lib/api";
import { Loan } from "@/lib/types";

const formSchema = z.object({
  customer_name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  whatsappNumber: z.string().min(10, { message: "Mobile number must be at least 10 digits." }),
  amount: z.coerce.number().min(1000, { message: "Amount must be at least 1000." }),
  interest_rate: z.coerce.number().min(1, { message: "Interest rate must be at least 1%." }),
  tenure: z.coerce.number().min(1, { message: "Tenure must be at least 1 month." }),
  firstDueDate: z.date({ required_error: "First due date is required." }),
  description: z.string().optional(),
});

interface LoanFormProps extends Partial<Loan> {
  isEditing?: boolean;
}

const LoanForm = ({ isEditing, ...loanData }: LoanFormProps) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customer_name: loanData?.customer_name || "",
      whatsappNumber: loanData?.whatsappNumber || "",
      amount: loanData?.amount || 0,
      interest_rate: loanData?.interest_rate || 0,
      tenure: loanData?.tenure || 12,
      firstDueDate: loanData?.firstDueDate ? new Date(loanData.firstDueDate) : new Date(),
      description: loanData?.description || "",
    },
  });

  useEffect(() => {
    if (id && !loanData?.customer_name) {
      const fetchLoan = async () => {
        try {
          setLoading(true);
          const loan = await getLoanById(id);
          if (loan) {
            form.reset({
              customer_name: loan.customer_name,
              whatsappNumber: loan.whatsappNumber,
              amount: loan.amount,
              interest_rate: loan.interest_rate,
              tenure: loan.tenure,
              firstDueDate: new Date(loan.firstDueDate),
              description: loan.description || "",
            });
          } else {
            toast.error("Loan not found");
            navigate("/loans");
          }
        } catch (error) {
          console.error("Error fetching loan:", error);
          toast.error("Failed to load loan details");
          navigate("/loans");
        } finally {
          setLoading(false);
        }
      };

      fetchLoan();
    }
  }, [id, loanData, form, navigate]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      
      if (id) {
        // Update existing loan
        await updateLoan(id, {
          customer_name: values.customer_name,
          whatsappNumber: values.whatsappNumber,
          amount: values.amount,
          interest_rate: values.interest_rate,
          tenure: values.tenure,
          firstDueDate: values.firstDueDate.toISOString(),
          description: values.description,
        });
        toast.success("Loan updated successfully");
        navigate(`/loans/${id}`);
      } else {
        // Create new loan
        await createLoan({
          customer_name: values.customer_name,
          whatsappNumber: values.whatsappNumber,
          amount: values.amount,
          interest_rate: values.interest_rate,
          tenure: values.tenure,
          firstDueDate: values.firstDueDate.toISOString(),
          description: values.description,
        });
        toast.success("Loan created successfully");
        navigate("/loans");
      }
    } catch (error) {
      console.error("Error submitting loan form:", error);
      toast.error("Failed to save loan");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <p>Loading loan details...</p>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="customer_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Customer Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter customer name" {...field} className="bg-background" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="whatsappNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>WhatsApp Number</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Enter WhatsApp number"
                  {...field}
                  className="bg-background"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Loan Amount</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Enter loan amount"
                    {...field}
                    className="bg-background"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="interest_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Interest Rate (%)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Enter interest rate"
                    {...field}
                    className="bg-background"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="tenure"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tenure (Months)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Enter tenure in months"
                    {...field}
                    className="bg-background"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstDueDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>First Due Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter loan description (optional)"
                  className="resize-none bg-background"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="button" variant="outline" onClick={() => navigate("/loans")} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />}
            {id ? "Update Loan" : "Create Loan"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default LoanForm;
